/**
 * 方法沉淀（`#method-skills`，批 2 由「优化打法」改名）——拷贝 Loop「优化打法」页签起步（用户 10-13：页面也要能看到，和 Loop 那页差不多）。
 * 仓库 `.claude/skills/<名>/SKILL.md` 的说明、版本历史与全文，只读同源，变更走 PR。
 * 合规修正（第二个提交）：版本历史走统一表格件（S3）、字号不低于 12（S4）、补失败态（S11）、全文改在抽屉里看（ref 03 §7）。
 * 批 3（D-03／D-04）：接口按范围过滤，本页只列 plan-* 与通用 skill；loop-* 在 Loop 页列；
 * 两份前端规范（组件形状标准、plan 页面设计）两边都不列。范围表在后端 store/method_skills.py。
 */
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import {
	type PlanMethodSkill,
	type PlanMethodSkillUpdate,
	planErrorMessage,
	usePlanMethodSkills,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import { Markdown } from "../kit/markdown";
import { type PlanColumn, PlanTable } from "../kit/plan-table";

const UPDATE_COLUMNS: PlanColumn<PlanMethodSkillUpdate>[] = [
	{
		key: "date",
		title: "日期",
		className: "w-28",
		render: (u) => <span className="font-mono">{u.date}</span>,
	},
	{
		key: "change",
		title: "改动（每行一版）",
		className: "max-w-[720px]",
		render: (u) => (
			<span className="block truncate text-text-secondary" title={u.change}>
				{u.change}
			</span>
		),
	},
	{
		key: "source",
		title: "出处",
		className: "max-w-[240px]",
		render: (u) => (
			<span className="block truncate text-text-muted" title={u.source}>
				{u.source}
			</span>
		),
	},
];

export function MethodSkillsTab() {
	const q = usePlanMethodSkills();
	const [open, setOpen] = useState<PlanMethodSkill | null>(null);
	const skills = q.data?.skills ?? [];

	if (q.isLoading)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	if (q.isError)
		return (
			<Card>
				<CardContent>
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-method-skills-fail"
					/>
				</CardContent>
			</Card>
		);
	if (skills.length === 0)
		return (
			<Card>
				<CardContent>
					<EmptyState
						title="没有找到 skill"
						desc="仓库 .claude/skills 下没有 SKILL.md。和预期不符时，说明仓库 skill 丢了，先查 git 历史。"
					/>
				</CardContent>
			</Card>
		);

	return (
		<div className="space-y-4" data-testid="plan-method-skills">
			{skills.map((s) => (
				<Card key={s.name} data-testid="plan-method-skill">
					<CardHeader>
						<CardTitle className="flex flex-wrap items-center gap-2">
							<span className="font-mono">{s.name}</span>
							<Badge tone={s.scope === "common" ? "neutral" : "accent"}>
								{s.scope === "common" ? "通用" : "Plan"}
							</Badge>
							<Badge tone="info">{s.versions} 版</Badge>
							{s.latest_update ? (
								<Badge tone="muted">最新 {s.latest_update}</Badge>
							) : (
								<Badge tone="warning">无版本日志</Badge>
							)}
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<p className="text-sm text-text-secondary">{s.description}</p>
						{s.updates.length ? (
							<PlanTable
								columns={UPDATE_COLUMNS}
								rows={s.updates}
								rowKey={(u) => `${u.date}|${u.change.slice(0, 40)}`}
								density="compact"
							/>
						) : null}
						<div className="flex items-center gap-3">
							<Button type="button" size="sm" variant="outline" onClick={() => setOpen(s)}>
								查看全文
							</Button>
							<span className="font-mono text-xs text-text-muted">{s.path}</span>
						</div>
					</CardContent>
				</Card>
			))}
			<Drawer
				open={!!open}
				onClose={() => setOpen(null)}
				width="min(1080px,95vw)"
				testId="plan-method-skill-drawer"
				title={<span className="font-mono">{open?.name}</span>}
				description={open?.path}
			>
				<DrawerSection>
					<Markdown text={open?.body ?? ""} testId="plan-method-skill-body" />
				</DrawerSection>
			</Drawer>
		</div>
	);
}
